import { useMemo, useState } from 'react'
import { setProjectStatus, type ProjectSummary } from '../lib/db'
import { money } from '../lib/quote'
import { IconCalendar, IconPhone, IconPin, IconTag } from './icons'
import { Sheet } from './bits'
import {
  board,

  cardNote,
  LEAD_STAGES,
  leadStage,
  moveTo,
  type LeadFacts,
  type LeadStage,
} from '../lib/leads'

/**
 * THE PIPELINE, DRAWN FROM WHAT HAPPENED.
 *
 * No card carries a column. Every one is computed from real milestones each
 * time this renders, which is why approving a scope on a job moves its card
 * without anybody touching the board - and why the board can never quietly
 * disagree with the job underneath it.
 *
 * EVERY CARD HAS A MOVE MENU, NOT ONLY A DRAG. Drag is a mouse affordance and
 * this is used on a phone in a driveway as often as at a desk, so the keyboard
 * and touch path is the primary one and drag is added on top of it.
 *
 * A MOVE SAYS WHAT IT WILL DO BEFORE IT DOES IT, and a refused move says why.
 * The refusals are the interesting part: dragging a card out of Won when its
 * scope is approved is not allowed at all, because the approval is the reason
 * the card is there.
 *
 * Empty columns stay on screen and stay empty. They show no records rather
 * than inventing work, and a column that vanished when empty would be a column
 * nobody could ever move anything into.
 */
export default function Pipeline({
  items,
  onOpenJob,
  onChanged,
}: {
  items: ProjectSummary[]
  onOpenJob: (p: ProjectSummary) => void
  onChanged: () => void
}) {
  const [asking, setAsking] = useState<{ job: LeadFacts; to: LeadStage } | null>(null)
  const [busy, setBusy] = useState('')
  const [dragging, setDragging] = useState<string | null>(null)

  const facts = useMemo<LeadFacts[]>(
    () =>
      items.map((j) => ({
        id: j.id,
        customer: j.customer,
        address: j.address,
        name: j.name,
        status: j.status,
        layers: j.layers,
        scopeApproved: j.work?.approved ?? false,
        balance: j.money?.balance ?? 0,
        hasPaper: !!j.money && j.money.total > 0,
        invoiced: j.money?.kind === 'invoice',
      })),
    [items],
  )

  const columns = board(facts)
  const jobOf = (id: string) => items.find((j) => j.id === id)

  /*
    WHEN IT WAS LAST TOUCHED, in the words a person uses out loud. A card in a
    pipeline is read for how stale it is as much as for whose it is.
  */
  const when = (iso: string) => {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const commit = async () => {
    if (!asking) return
    const m = moveTo(asking.job, asking.to)
    if (!m.ok || !m.status) return
    setBusy('Saving...')
    await setProjectStatus(asking.job.id, m.status)
    setBusy('')
    setAsking(null)
    onChanged()
  }

  return (
    <>
      {/*
        No swipe hint. A column that stops short of the right hand edge with
        the next one showing behind it says the same thing without a sentence,
        and the design file's board says nothing at all.
      */}
      <div className="pipe">
        {columns.map((col) => (
          <section
            key={col.stage}
            className={`pipe-col${dragging ? ' live' : ''}`}
            onDragOver={(e) => {
              if (dragging) e.preventDefault()
            }}
            onDrop={() => {
              const j = facts.find((f) => f.id === dragging)
              setDragging(null)
              if (j) setAsking({ job: j, to: col.stage })
            }}
          >
            <header className="pipe-head">
              <h3>{col.label}</h3>
              <span className="pipe-n">{col.cards.length}</span>
            </header>
            <p className="pipe-blurb">{col.blurb}</p>

            <div className="pipe-cards">
              {col.cards.length === 0 ? (
                /* No records. Not a placeholder that looks like work. */
                <p className="pipe-none">No jobs in this stage.</p>
              ) : (
                col.cards.map((c) => {
                  /*
                    A CARD IS A FACE, A NAME, AND THE THREE THINGS YOU WOULD
                    ASK ABOUT IT. The design file's lead card carries a
                    portrait, the name, the estimate number, then the phone,
                    the site and the service, then a footer with a date on one
                    side and a count on the other. Ours carries the house
                    rather than a portrait, because in this trade you recognize
                    the roof before the surname, and everything on it is
                    already on the job.
                  */
                  const j = jobOf(c.id)
                  const who = c.customer.trim() || c.address.trim() || c.name
                  const note = cardNote(c)
                  return (
                  <div
                    key={c.id}
                    className="pipe-card"
                    draggable
                    onDragStart={() => setDragging(c.id)}
                    onDragEnd={() => setDragging(null)}
                  >
                    <button
                      className="pipe-open"
                      onClick={() => {
                        if (j) onOpenJob(j)
                      }}
                    >
                      <span className="pipe-id">
                        <span className="pipe-face">
                          {j?.thumb ? <img src={j.thumb} alt="" /> : <i />}
                        </span>
                        <span className="pipe-idn">
                          <strong>{who}</strong>
                          <em>{j?.money?.number || 'No quote yet'}</em>
                        </span>
                      </span>

                      <span className="pipe-facts">
                        {c.address.trim() && (
                          <span>
                            <IconPin size={14} />
                            {c.address}
                          </span>
                        )}
                        {j?.phone?.trim() && (
                          <span>
                            <IconPhone size={14} />
                            {j.phone}
                          </span>
                        )}
                        <span>
                          <IconTag size={14} />
                          {c.name?.trim() || 'Untitled mockup'}
                        </span>
                      </span>
                    </button>

                    {/*
                      The footer is the two facts you scan a column for: how
                      old it is, and what is riding on it.
                    */}
                    <div className="pipe-foot">
                      <span>
                        <IconCalendar size={14} />
                        {j ? when(j.savedAt) : ''}
                      </span>
                      <span className="pipe-foot-r">
                        {note ?? (j?.money && j.money.total > 0 ? money(j.money.total) : '')}
                      </span>
                    </div>

                    {/*
                      The move menu. A plain select, because it is reachable by
                      keyboard, by a screen reader and by a thumb without any of
                      the three needing to know what a drag is.
                    */}
                    <label className="pipe-move">
                      <span className="sr">Move {c.customer || c.name}</span>
                      <select
                        value=""
                        onChange={(e) => {
                          const to = e.target.value as LeadStage
                          e.target.value = ''
                          if (to) setAsking({ job: c, to })
                        }}
                      >
                        <option value="">Move</option>
                        {LEAD_STAGES.filter((s) => s.id !== leadStage(c)).map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  )
                })
              )}
            </div>
          </section>
        ))}
      </div>

      {asking && <MoveSheet ask={asking} busy={busy} onClose={() => setAsking(null)} onGo={commit} />}
    </>
  )
}

/**
 * WHAT THIS WILL DO, BEFORE IT DOES IT.
 *
 * Every move goes through here, including a drag, so the drag cannot be the
 * one path that skips the explanation. A refused move shows only the reason
 * and a way out.
 */
function MoveSheet({
  ask,
  busy,
  onClose,
  onGo,
}: {
  ask: { job: LeadFacts; to: LeadStage }
  busy: string
  onClose: () => void
  onGo: () => void
}) {
  const m = moveTo(ask.job, ask.to)
  const to = LEAD_STAGES.find((s) => s.id === ask.to)!
  const who = ask.job.customer.trim() || ask.job.address.trim() || ask.job.name

  return (
    <Sheet
      title={`${who} to ${to.label}`}

      onClose={onClose}
    >
      {m.ok ? (
        <>
          <p style={{ margin: '0 0 14px' }}>{m.does}</p>
          <div className="ws-actions">
            <button className="btn primary" onClick={onGo} disabled={!!busy}>
              {busy || 'Do it'}
            </button>
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <p style={{ margin: '0 0 14px' }}>{m.because}</p>
          <div className="ws-actions">
            <button className="btn" onClick={onClose}>
              Close
            </button>
          </div>
        </>
      )}
    </Sheet>
  )
}
